/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Execution monitor for LogicFlow executions — displays execution list, step details, and message log with severity filtering and resolution actions.
 */
import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsCrudService} from '../../../../../core/shared/service/crud/mvs-crud.service';
import {LogicFlowService} from '../../../service/api/logic-flow.service';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {QlService} from '../../../../../core/shared/ql/service/ql.service';
import {LogicFlowExecutionDto} from '../../../model/dto/entity/logic-flow-execution.dto';
import {LogicFlowExecutionStepDto} from '../../../model/dto/entity/logic-flow-execution-step.dto';
import {LogicFlowMessageDto} from '../../../model/dto/entity/logic-flow-message.dto';
import {LogicFlowExecutionStatusEnum} from '../../../enum/logic-flow-execution-status.enum';
import {LogicFlowExecutionModeEnum} from '../../../enum/logic-flow-execution-mode.enum';
import {LogicFlowStepExecutionStatusEnum} from '../../../enum/logic-flow-step-execution-status.enum';
import {LogicFlowMessageSeverityEnum} from '../../../enum/logic-flow-message-severity.enum';

/**
 * Helper interface for execution status display configuration.
 */
type TagSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';

interface StatusDisplayConfig {
    label: string;
    severity: TagSeverity;
    icon: string;
}

/**
 * Helper interface for message severity display configuration.
 */
interface SeverityDisplayConfig {
    label: string;
    severity: TagSeverity;
    icon: string;
}

@Component({
    selector: 'mvs-lg-execution-monitor',
    templateUrl: './lg-execution-monitor.component.html',
    styleUrls: ['./lg-execution-monitor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LgExecutionMonitorComponent implements OnInit, OnChanges, OnDestroy {

    // --- State flags ---
    busy: boolean = false;
    initialized: boolean = false;
    loadingSteps: boolean = false;
    loadingMessages: boolean = false;
    errorMessage: string | null = null;

    // --- Inputs ---
    @Input() flowId: number;
    @Input() executionId: number;

    // --- Outputs ---
    @Output() executionSelected = new EventEmitter<number>();

    // --- Data ---
    executionList: LogicFlowExecutionDto[] = [];
    selectedExecution: LogicFlowExecutionDto | null = null;
    executionSteps: LogicFlowExecutionStepDto[] = [];
    messages: LogicFlowMessageDto[] = [];
    filteredMessages: LogicFlowMessageDto[] = [];

    // --- Filter ---
    statusFilterOptions: { label: string; value: LogicFlowExecutionStatusEnum | null }[] = [];
    selectedStatusFilter: LogicFlowExecutionStatusEnum | null = null;

    // --- Severity filter ---
    severityFilters: { severity: LogicFlowMessageSeverityEnum; active: boolean; label: string; icon: string }[] = [];

    // --- Expanded step tracking ---
    expandedStepIds: Set<number> = new Set();

    // --- Enum references for template ---
    readonly ExecutionStatus = LogicFlowExecutionStatusEnum;
    readonly ExecutionMode = LogicFlowExecutionModeEnum;
    readonly StepStatus = LogicFlowStepExecutionStatusEnum;
    readonly MessageSeverity = LogicFlowMessageSeverityEnum;

    // --- Services ---
    private executionService: MvsCrudService;

    // --- Subscriptions ---
    private subscriptions: Subscription[] = [];

    // --- Status display mapping ---
    readonly executionStatusConfig: Record<number, StatusDisplayConfig> = {
        [LogicFlowExecutionStatusEnum.CREATED]: {label: 'Erstellt', severity: 'secondary', icon: 'fa-regular fa-circle'},
        [LogicFlowExecutionStatusEnum.RUNNING]: {label: 'Laufend', severity: 'info', icon: 'fa-regular fa-spinner-third fa-spin'},
        [LogicFlowExecutionStatusEnum.PAUSED]: {label: 'Pausiert', severity: 'warn', icon: 'fa-regular fa-pause'},
        [LogicFlowExecutionStatusEnum.COMPLETED]: {label: 'Abgeschlossen', severity: 'success', icon: 'fa-regular fa-circle-check'},
        [LogicFlowExecutionStatusEnum.FAILED]: {label: 'Fehlgeschlagen', severity: 'danger', icon: 'fa-regular fa-circle-xmark'}
    };

    readonly stepStatusConfig: Record<number, StatusDisplayConfig> = {
        [LogicFlowStepExecutionStatusEnum.PENDING]: {label: 'Ausstehend', severity: 'secondary', icon: 'fa-regular fa-clock'},
        [LogicFlowStepExecutionStatusEnum.RUNNING]: {label: 'Laufend', severity: 'info', icon: 'fa-regular fa-spinner-third fa-spin'},
        [LogicFlowStepExecutionStatusEnum.COMPLETED]: {label: 'Abgeschlossen', severity: 'success', icon: 'fa-regular fa-circle-check'},
        [LogicFlowStepExecutionStatusEnum.SKIPPED]: {label: 'Übersprungen', severity: 'warn', icon: 'fa-regular fa-forward'},
        [LogicFlowStepExecutionStatusEnum.FAILED]: {label: 'Fehlgeschlagen', severity: 'danger', icon: 'fa-regular fa-circle-xmark'},
        [LogicFlowStepExecutionStatusEnum.PAUSED]: {label: 'Pausiert', severity: 'warn', icon: 'fa-regular fa-pause'}
    };

    readonly severityConfig: Record<number, SeverityDisplayConfig> = {
        [LogicFlowMessageSeverityEnum.INFO]: {label: 'Info', severity: 'info', icon: 'fa-regular fa-circle-info'},
        [LogicFlowMessageSeverityEnum.WARNING]: {label: 'Warnung', severity: 'warn', icon: 'fa-regular fa-triangle-exclamation'},
        [LogicFlowMessageSeverityEnum.ERROR]: {label: 'Fehler', severity: 'danger', icon: 'fa-regular fa-circle-xmark'},
        [LogicFlowMessageSeverityEnum.DECISION_REQUIRED]: {label: 'Entscheidung erforderlich', severity: 'contrast', icon: 'fa-regular fa-circle-question'},
        [LogicFlowMessageSeverityEnum.TRACE]: {label: 'Trace', severity: 'secondary', icon: 'fa-regular fa-shoe-prints'},
        [LogicFlowMessageSeverityEnum.DEBUG]: {label: 'Debug', severity: 'secondary', icon: 'fa-regular fa-bug'}
    };

    readonly modeLabels: Record<number, string> = {
        [LogicFlowExecutionModeEnum.NORMAL]: 'Normal',
        [LogicFlowExecutionModeEnum.DEBUG]: 'Debug',
        [LogicFlowExecutionModeEnum.DRY_RUN]: 'Trockenlauf'
    };

    constructor(
        protected coreService: MvsCoreService,
        protected logicFlowService: LogicFlowService,
        protected cd: ChangeDetectorRef,
        protected qlService: QlService
    ) {
    }

    ngOnInit(): void {
        this.initServices();
        this.initFilters();
        this.refreshComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['flowId']) {
            this.resetState();
            this.refreshComponent();
        }

        if (changes['executionId'] && this.executionId) {
            this.loadExecutionById(this.executionId);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    // =========================================================================
    // Initialization
    // =========================================================================

    /**
     * Initialize CRUD services via MvsCoreService dynamic resolution.
     */
    private initServices(): void {
        this.executionService = this.coreService.getCrudService('lg.LogicFlowExecution');
    }

    /**
     * Initialize status filter options and severity filter toggles.
     */
    private initFilters(): void {
        this.statusFilterOptions = [
            {label: 'Alle', value: null},
            {label: 'Erstellt', value: LogicFlowExecutionStatusEnum.CREATED},
            {label: 'Laufend', value: LogicFlowExecutionStatusEnum.RUNNING},
            {label: 'Pausiert', value: LogicFlowExecutionStatusEnum.PAUSED},
            {label: 'Abgeschlossen', value: LogicFlowExecutionStatusEnum.COMPLETED},
            {label: 'Fehlgeschlagen', value: LogicFlowExecutionStatusEnum.FAILED}
        ];

        this.severityFilters = [
            {severity: LogicFlowMessageSeverityEnum.INFO, active: true, label: 'Info', icon: 'fa-regular fa-circle-info'},
            {severity: LogicFlowMessageSeverityEnum.WARNING, active: true, label: 'Warnung', icon: 'fa-regular fa-triangle-exclamation'},
            {severity: LogicFlowMessageSeverityEnum.ERROR, active: true, label: 'Fehler', icon: 'fa-regular fa-circle-xmark'},
            {severity: LogicFlowMessageSeverityEnum.DECISION_REQUIRED, active: true, label: 'Entscheidung', icon: 'fa-regular fa-circle-question'},
            {severity: LogicFlowMessageSeverityEnum.TRACE, active: true, label: 'Trace', icon: 'fa-regular fa-shoe-prints'},
            {severity: LogicFlowMessageSeverityEnum.DEBUG, active: true, label: 'Debug', icon: 'fa-regular fa-bug'}
        ];
    }

    // =========================================================================
    // Data Loading
    // =========================================================================

    /**
     * Refresh the execution list from the backend.
     */
    refreshComponent(): void {
        if (!this.flowId) {
            return;
        }

        this.busy = true;
        this.errorMessage = null;

        const filters: FilterCriteria[] = [
            FilterCriteria.create('e.logicFlowId', FilterCriteria.cOperatorEqual, this.flowId)
        ];

        if (this.selectedStatusFilter !== null && this.selectedStatusFilter !== undefined) {
            filters.push(
                FilterCriteria.create('e.executionStatus', FilterCriteria.cOperatorEqual, this.selectedStatusFilter)
            );
        }

        const sub = this.qlService.builder()
            .query({
                name: 'executions',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowExecution',
                    as: 'e'
                },
                filters: filters,
                sortings: [
                    new Sorting('e.startedAt', false)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.executionList = result.get('executions') as LogicFlowExecutionDto[];
                    this.busy = false;
                    this.initialized = true;
                    this.cd.markForCheck();
                },
                error: (err) => {
                    this.errorMessage = 'Fehler beim Laden der Ausführungen.';
                    this.busy = false;
                    this.initialized = true;
                    this.cd.markForCheck();
                    console.error('Error loading executions:', err);
                }
            });

        this.subscriptions.push(sub);
    }

    /**
     * Load a single execution by ID and select it.
     */
    private loadExecutionById(executionId: number): void {
        const execution = this.executionList.find(e => e.id === executionId);
        if (execution) {
            this.selectExecution(execution);
        } else {
            // Execution not in current list, load it directly
            const sub = this.executionService.get(executionId).subscribe({
                next: (result: LogicFlowExecutionDto) => {
                    if (result) {
                        this.selectedExecution = result;
                        this.loadExecutionDetails(result.id);
                        this.executionSelected.emit(result.id);
                        this.cd.markForCheck();
                    }
                },
                error: () => {
                    this.cd.markForCheck();
                }
            });
            this.subscriptions.push(sub);
        }
    }

    /**
     * Load execution steps and messages for a given execution.
     */
    private loadExecutionDetails(executionId: number): void {
        this.loadSteps(executionId);
        this.loadMessages(executionId);
    }

    /**
     * Load execution steps for a given execution.
     */
    private loadSteps(executionId: number): void {
        this.loadingSteps = true;
        this.expandedStepIds.clear();

        const sub = this.qlService.builder()
            .query({
                name: 'executionSteps',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowExecutionStep',
                    as: 'e',
                    joins: [
                        {
                            type: 'entity',
                            name: 'flowStep',
                            as: 'fs',
                            joinType: 'left',
                            fields: [
                                {name: 'useMockOutput'}
                            ]
                        }
                    ]
                },
                filters: [
                    FilterCriteria.create('e.executionId', FilterCriteria.cOperatorEqual, executionId)
                ],
                sortings: [
                    new Sorting('e.startedAt', true)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.executionSteps = result.get('executionSteps') as LogicFlowExecutionStepDto[];
                    this.loadingSteps = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.executionSteps = [];
                    this.loadingSteps = false;
                    this.cd.markForCheck();
                }
            });

        this.subscriptions.push(sub);
    }

    /**
     * Load messages for a given execution.
     */
    private loadMessages(executionId: number): void {
        this.loadingMessages = true;

        const sub = this.qlService.builder()
            .query({
                name: 'executionMessages',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'lg.LogicFlowMessage',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create('e.executionId', FilterCriteria.cOperatorEqual, executionId)
                ],
                sortings: [
                    new Sorting('e.createdDate', true)
                ]
            })
            .execute()
            .subscribe({
                next: (result) => {
                    this.messages = result.get('executionMessages') as LogicFlowMessageDto[];
                    this.applyMessageSeverityFilter();
                    this.loadingMessages = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.messages = [];
                    this.filteredMessages = [];
                    this.loadingMessages = false;
                    this.cd.markForCheck();
                }
            });

        this.subscriptions.push(sub);
    }

    // =========================================================================
    // User Actions
    // =========================================================================

    /**
     * Select an execution and load its details.
     */
    selectExecution(execution: LogicFlowExecutionDto): void {
        if (this.selectedExecution?.id === execution.id) {
            // Deselect
            this.selectedExecution = null;
            this.executionSteps = [];
            this.messages = [];
            this.filteredMessages = [];
            this.cd.markForCheck();
            return;
        }

        this.selectedExecution = execution;
        this.loadExecutionDetails(execution.id);
        this.executionSelected.emit(execution.id);
    }

    /**
     * Toggle step expansion.
     */
    toggleStepExpansion(stepId: number): void {
        if (this.expandedStepIds.has(stepId)) {
            this.expandedStepIds.delete(stepId);
        } else {
            this.expandedStepIds.add(stepId);
        }
        this.cd.markForCheck();
    }

    /**
     * Check whether a step is expanded.
     */
    isStepExpanded(stepId: number): boolean {
        return this.expandedStepIds.has(stepId);
    }

    /**
     * Refresh button handler.
     */
    onRefresh(): void {
        if (this.selectedExecution) {
            this.loadExecutionDetails(this.selectedExecution.id);
        }
        this.refreshComponent();
    }

    /**
     * Status filter changed.
     */
    onStatusFilterChange(): void {
        this.selectedExecution = null;
        this.executionSteps = [];
        this.messages = [];
        this.filteredMessages = [];
        this.refreshComponent();
    }

    /**
     * Toggle severity filter and re-apply.
     */
    toggleSeverityFilter(index: number): void {
        this.severityFilters[index].active = !this.severityFilters[index].active;
        this.applyMessageSeverityFilter();
        this.cd.markForCheck();
    }

    /**
     * Apply severity filter to messages.
     */
    private applyMessageSeverityFilter(): void {
        const activeSeverities = this.severityFilters
            .filter(f => f.active)
            .map(f => f.severity);

        this.filteredMessages = this.messages.filter(m =>
            activeSeverities.includes(m.severity)
        );
    }

    /**
     * Resolve a DECISION_REQUIRED message.
     */
    resolveMessage(message: LogicFlowMessageDto): void {
        if (message.resolved) {
            return;
        }

        const sub = this.logicFlowService.resolveMessage(message.id).subscribe({
            next: (result: LogicFlowMessageDto) => {
                // Update the message in our local arrays
                const updateMessage = (msg: LogicFlowMessageDto) => {
                    msg.resolved = result.resolved ?? true;
                    msg.resolvedAt = result.resolvedAt;
                    msg.resolvedByUserDtoName = result.resolvedByUserDtoName;
                    msg.resolvedByUserDtoId = result.resolvedByUserDtoId;
                    msg.resolutionNote = result.resolutionNote;
                };

                const msgInList = this.messages.find(m => m.id === message.id);
                if (msgInList) {
                    updateMessage(msgInList);
                }
                const msgInFiltered = this.filteredMessages.find(m => m.id === message.id);
                if (msgInFiltered) {
                    updateMessage(msgInFiltered);
                }

                this.cd.markForCheck();
            },
            error: (err) => {
                console.error('Error resolving message:', err);
            }
        });

        this.subscriptions.push(sub);
    }

    // =========================================================================
    // Helpers
    // =========================================================================

    /**
     * Reset all state on flow change.
     */
    private resetState(): void {
        this.selectedExecution = null;
        this.executionList = [];
        this.executionSteps = [];
        this.messages = [];
        this.filteredMessages = [];
        this.expandedStepIds.clear();
        this.selectedStatusFilter = null;
        this.errorMessage = null;
    }

    /**
     * Calculate duration between two ISO date strings in milliseconds.
     */
    calculateDuration(startedAt: string | undefined, completedAt: string | undefined): number | null {
        if (!startedAt || !completedAt) {
            return null;
        }
        const start = new Date(startedAt).getTime();
        const end = new Date(completedAt).getTime();
        return end - start;
    }

    /**
     * Format duration in ms to a human-readable string.
     */
    formatDuration(durationMs: number | null): string {
        if (durationMs === null || durationMs === undefined) {
            return '-';
        }
        if (durationMs < 1000) {
            return `${durationMs} ms`;
        }
        const seconds = Math.floor(durationMs / 1000);
        const remainingMs = durationMs % 1000;
        if (seconds < 60) {
            return `${seconds},${String(remainingMs).padStart(3, '0').substring(0, 1)} s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes} min ${remainingSeconds} s`;
    }

    /**
     * Format a JSON string for display.
     */
    formatJson(jsonString: string | undefined): string {
        if (!jsonString) {
            return '-';
        }
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2);
        } catch {
            return jsonString;
        }
    }

    /**
     * Get status display config safely.
     */
    getExecutionStatusConfig(status: LogicFlowExecutionStatusEnum): StatusDisplayConfig {
        return this.executionStatusConfig[status] || {label: 'Unbekannt', severity: 'secondary', icon: 'fa-regular fa-circle-question'};
    }

    /**
     * Get step status display config safely.
     */
    getStepStatusConfig(status: LogicFlowStepExecutionStatusEnum): StatusDisplayConfig {
        return this.stepStatusConfig[status] || {label: 'Unbekannt', severity: 'secondary', icon: 'fa-regular fa-circle-question'};
    }

    /**
     * Get severity display config safely.
     */
    getSeverityConfig(severity: LogicFlowMessageSeverityEnum): SeverityDisplayConfig {
        return this.severityConfig[severity] || {label: 'Unbekannt', severity: 'secondary', icon: 'fa-regular fa-circle-question'};
    }

    /**
     * Get execution mode label.
     */
    getModeLabel(mode: LogicFlowExecutionModeEnum): string {
        return this.modeLabels[mode] || 'Unbekannt';
    }
}
